/* eslint-disable max-len */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { HStack, ListItem, Popover, PopoverBody, PopoverContent, PopoverTrigger, Stack, Text, UnorderedList, VStack } from '@chakra-ui/react'
import { useDisplayModal, useEngine, usePin, useShareVehicle, useVehicles } from 'context'
import { EngineFormType, ShareVehicleConfirmPinType } from 'context/types'
import { CONFIRM_PIN_TO_SHARE_VEHICLE_SCHEMA } from 'context/validations'
import { Formik } from 'formik'
import { ConfirmToPinParamList, GenericRouteParamList } from 'router/types'
import { themeColors } from 'theme'

import { CardCustomResult } from 'components/CardCustomResult'
import { CardBody, CardContainer } from 'components/CustomCard'
import { CustomModal } from 'components/CustomModal'
import { PinInputControl, SubmitButton } from 'components/InputCustom'
import {
  InfoIcon, LockedIcon, LockedSuccessIcon, QuestionTooltipIcon, UnlockedIcon, UnlockedSuccessIcon
} from 'components/SVGS'
import { customToast } from 'components/ToastCustom'

const { icon: { infoIconColor, primaryIconColor,logoIconColor } } = themeColors

export const ConfirmToPin = () => {
  const navigation = useNavigate()
  const location = useLocation()
  const {
    getConfirmPinToShareVehicle,
    clearConfirmPin,
    clearError: shareClearError,
    shareVehicleState: { loading: shareLoading, error: errorPin },
  } = useShareVehicle()
  const { pinState: { state: { isHavePin }, error } } = usePin()
  const { vehicleState: { state: { vehicle } } } = useVehicles()
  const { openDialog, closeModal } = useDisplayModal()
  const [params, setParams] = useState<ConfirmToPinParamList | null>(null)

  const {
    lockEngine,
    unlockEngine,
    clearEngineMessage,
    clearError,
    engineState: {
      error: engineError,
      loading,
      state: { engineBlocked, lockEngineMessage },
    },
  } = useEngine()

  useEffect(() => {
    let params = null

    if (location.state) ({ params } = location.state as GenericRouteParamList<ConfirmToPinParamList>)
    params && setParams(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    if (loading && engineBlocked !== null)
      openDialog({
        content: <CustomModal
          type='secondary'
          title={engineBlocked ? 'Desbloqueando el motor...' : 'Bloqueando el motor...'} />,
        variant: 'secondary'
      })
    else if (lockEngineMessage !== '' || error) closeModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, lockEngineMessage, error, engineBlocked])

  useEffect(() => {
    if (!isHavePin) return navigation('/my-vehicles')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHavePin])

  useEffect(() => {
    if (!vehicle) return navigation('/my-vehicles')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle])

  useEffect(() => {
    engineError && customToast.showToast({ description: engineError })
    clearError()
    clearEngineMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineError])

  useEffect(() => {
    errorPin && customToast.showToast({ description: errorPin })
    shareClearError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorPin])

  useEffect(() => {
    if (engineBlocked === null)
      return customToast.showToast({
        description: 'No se pudo determinar el estado actual del motor, por favor intente de nuevamente.'
      }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineBlocked])

  const initialValues: EngineFormType = {
    pin: ''
  }

  const handleGoBack = () => {
    navigation('/my-vehicles')
    clearEngineMessage()
  }

  const handleOnSubmit = async (data: ShareVehicleConfirmPinType) => {
    const success = await getConfirmPinToShareVehicle(data)

    if (success)
      if (params?.previousRoute === 'HOME')
        if (engineBlocked)
          unlockEngine(data, vehicle?.id || '')
        else
          lockEngine(data, vehicle?.id || '')

      else {
        clearConfirmPin()
        params?.vehicleInfo && navigation('/add-contact-share-vehicle', { replace: true })
      }
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={CONFIRM_PIN_TO_SHARE_VEHICLE_SCHEMA}>
      <CardContainer
        isScreen
        onClose={handleGoBack}
        hiddenStack={lockEngineMessage !== ''}
        headerText={params?.previousRoute === 'VEHICLE'
          ? 'Compartir vehículo '
          : engineBlocked === null
            ? 'Sin información'
            : engineBlocked
              ? 'Desbloquea el motor de tu vehículo'
              : 'Bloquea el motor de tu vehículo'}
      >
        <CardBody isBorder>
          {lockEngineMessage === '' ?
            <>
              <Stack>
                {engineBlocked || params?.previousRoute !== 'HOME'
                  ? <LockedIcon size={7} fill={primaryIconColor} />
                  : <UnlockedIcon size={7} fill={primaryIconColor} />
                }
              </Stack>
              <HStack justifyContent={'center'} alignItems={'flex-start'}>
                <VStack justifyContent={'center'} alignItems={'center'}>
                  <Text textAlign={'center'} variant={'LG_SB'}>
                    {params?.previousRoute === 'HOME' ? params.text : 'Ingresa tu PIN'}
                  </Text>
                  <Text hidden={params?.previousRoute === 'HOME'} textAlign={'center'} variant={'SM'}>
                    {params?.text}
                  </Text>
                </VStack>
                <Stack>
                  <Popover placement='top' variant={'responsive'}>
                    {({ isOpen }) => (
                      <>
                        <PopoverTrigger>
                          <span>
                            <QuestionTooltipIcon size={7} fill={isOpen ? primaryIconColor : infoIconColor} />
                          </span>
                        </PopoverTrigger>
                        <PopoverContent bgSize={{ base: 'xs', md: 'md' }}>
                          <PopoverBody>
                            Tu PIN es de 4 números, en caso de no tenerlo puedes crearlo desde tu perfil.
                          </PopoverBody>
                        </PopoverContent>
                      </>
                    )}
                  </Popover>
                </Stack>
              </HStack>
              <HStack>
                <PinInputControl
                  pinAmount={4}
                  pinInputProps={{ errorBorderColor: 'red.500', mask: true, isDisabled: loading }}
                  name='pin'
                  placeholder='INGRESAR PIN'
                  label='' />
              </HStack>
              <SubmitButton disabled={engineBlocked === null}
                w={'full'}
                loadingText={'Validando...'}
                isLoading={loading || shareLoading}
                mx={'auto'}>
                {engineBlocked !== null ? params?.textButton : 'Sin Información'}
              </SubmitButton>
            </>
            :
            <CardCustomResult
              headerTitle={lockEngineMessage}
              onSubmit={handleGoBack}
              type={'custom'}
              icon={
                engineBlocked ?
                  LockedSuccessIcon
                  :
                  UnlockedSuccessIcon
              }
              fill={primaryIconColor}
              size={'70px'}
            />
          }
        </CardBody>
        <HStack
          hidden={params?.previousRoute !== 'HOME' || lockEngineMessage !== ''}
          w={{ base: 'full', md: 460 }}
          justifyContent={'center'}
          alignItems={'flex-start'}
          spacing={2}>
          <VStack align="start" spacing={1} marginX={10}>
           <HStack position={'relative'} marginLeft={-7}>
            <InfoIcon  size={7} fill={logoIconColor} />
            <Text fontWeight={'bold'} variant={{ base: 'XL' }}>IMPORTANTE:</Text>
            </HStack>
          <UnorderedList>
              <ListItem>Recuerda no bloquear tu vehículo por más de 6 horas para evitar consumir la batería del vehículo.</ListItem>
              <ListItem>Evita bloquear motor cuando tu vehículo se encuentre en un subterráneo.</ListItem>
              <ListItem>Al bloquear motor por un período prolongado, es necesario mantener contacto del vehículo para volver a energizar el GPS. Luego debes volver a intentar.</ListItem>
          </UnorderedList>
          </VStack>
        </HStack>
      </CardContainer>
    </Formik>
  )
}
